/**
 * Socket.IO-backed EventBus implementation.
 *
 * Same public interface as `mock-bus.ts` (the `EventBus` shape). The choice
 * between mock and socket is made in `index.ts`: if `configureBus()` was
 * called with a `serverUrl`, the socket implementation is used.
 *
 * Auth is passed via a `authProvider` callback so callers can return the
 * current token at connect time (the agent app rotates JWTs; the function
 * lets us read the latest one without rewiring the bus).
 */

import { io, type Socket } from 'socket.io-client';
import type { BusEvent, BusEventName, BusEventOf } from './schema';

export type BusUnsubscribe = () => void;
export type BusListener<T extends BusEventName = BusEventName> = (
  event: BusEventOf<T>,
) => void;

export type ConnectionState = 'connecting' | 'open' | 'closed' | 'disconnected';

export interface NetworkConditions {
  minLatency: number;
  maxLatency: number;
  packetLoss: number;
  disconnected: boolean;
}

export interface EventBus {
  emit<T extends BusEventName>(event: BusEventOf<T>): void;
  on<T extends BusEventName>(name: T, listener: BusListener<T>): BusUnsubscribe;
  once<T extends BusEventName>(name: T, listener: BusListener<T>): BusUnsubscribe;
  off<T extends BusEventName>(name: T, listener: BusListener<T>): void;
  close(): void;
  /**
   * Try to (re)open the connection. No-op if already open. Useful after the
   * authProvider has fresh credentials — e.g. agent just logged in.
   */
  reconnect(): void;
  setNetworkConditions(conditions: Partial<NetworkConditions>): void;
  getNetworkConditions(): NetworkConditions;
  inspect(): { listenerCount: Record<string, number>; connected: boolean };
  onConditionsChange(listener: (c: NetworkConditions) => void): BusUnsubscribe;
  onConnectionChange(listener: (state: ConnectionState) => void): BusUnsubscribe;
  getConnectionState(): ConnectionState;
}

export type VisitorAuth = {
  kind: 'visitor';
  siteToken: string;
  visitorId: string;
  /** Optional reconnect cursor for server-to-visitor transcript replay. */
  sessionId?: string;
  lastSequence?: number;
};
export type AgentAuth = {
  kind: 'agent';
  jwt: string;
};
export type SocketAuth = VisitorAuth | AgentAuth;

export interface SocketBusConfig {
  serverUrl: string;
  authProvider: () => SocketAuth;
  /**
   * Skip the automatic connect at construction and wait for an explicit
   * `reconnect()`.
   *
   * The widget uses this for sites that don't want browsing visitors on the
   * live monitor: without it, every page view opens a socket and starts a
   * presence heartbeat whether or not the visitor ever engages.
   */
  deferConnect?: boolean;
  /**
   * Durable home for transcript messages the server has not acknowledged yet.
   * Without it the reliable outbox lives only in memory, so a reload while
   * offline silently drops a message the sender already sees in their
   * transcript. Restored events are re-sent on the next connect, never
   * re-dispatched locally; the server dedupes them by message id.
   */
  outboxStorage?: OutboxStorage;
  /**
   * Called once the server has committed a transcript message, with its
   * conversation sequence. Lets a client stamp its own optimistic messages so
   * its reconnect cursor has no holes where its own messages sit.
   */
  onCommitted?: (key: string, sequence: number | undefined) => void;
}

export interface OutboxStorage {
  load(): BusEvent[];
  save(events: BusEvent[]): void;
}

const DEFAULT_CONDITIONS: NetworkConditions = {
  minLatency: 0, // network is real now — no synthetic latency by default
  maxLatency: 0,
  packetLoss: 0,
  disconnected: false,
};

const BUS_CHANNEL = 'bus';
const ACK_TIMEOUT_MS = 5_000;
const RETRY_MAX_MS = 10_000;

interface AckResponse {
  ok: boolean;
  reason?: 'rate-limited' | 'invalid-payload' | 'forbidden' | 'internal';
  /** Conversation sequence of a committed transcript message. */
  sequence?: number;
}

interface PendingDelivery {
  event: BusEvent;
  attempts: number;
  generation: number;
  inFlight: boolean;
  timer?: ReturnType<typeof setTimeout>;
}

/** Client-generated idempotency key for durable transcript messages. */
export function deliveryKey(event: BusEvent): string | null {
  if (event.type === 'visitor:chat:message' || event.type === 'agent:chat:message') {
    return event.message.messageId;
  }
  if (event.type === 'system:message') return event.messageId;
  return null;
}

type AnyBusListener = (event: BusEvent) => void;

export function createSocketBus(config: SocketBusConfig): EventBus {
  const listeners = new Map<BusEventName, Set<AnyBusListener>>();
  const conditionListeners = new Set<(c: NetworkConditions) => void>();
  const connectionListeners = new Set<(s: ConnectionState) => void>();
  let conditions: NetworkConditions = { ...DEFAULT_CONDITIONS };
  let connection: ConnectionState = 'connecting';
  let closed = false;
  const pendingOutbox: BusEvent[] = [];
  const reliableOutbox = new Map<string, PendingDelivery>();
  // Messages left unacknowledged by a previous page load. Queued (in their
  // original order) before the first connect, whose handler flushes them.
  try {
    for (const event of config.outboxStorage?.load() ?? []) {
      const key = deliveryKey(event);
      if (key) queueReliable(event, key);
    }
  } catch {
    // Corrupt or unreadable storage: start with an empty outbox.
  }

  const socket: Socket = io(config.serverUrl, {
    transports: ['websocket'],
    autoConnect: false,
    auth: (cb) => cb(config.authProvider() as unknown as Record<string, unknown>),
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });

  function setConnection(next: ConnectionState) {
    if (connection === next) return;
    connection = next;
    [...connectionListeners].forEach((fn) => fn(next));
  }

  /** Returns true when authProvider yields something the server can verify. */
  function hasValidAuth(): boolean {
    try {
      const a = config.authProvider();
      if (!a || typeof a !== 'object' || typeof (a as { kind?: unknown }).kind !== 'string') return false;
      if (a.kind === 'agent') return typeof a.jwt === 'string' && a.jwt.length > 0;
      if (a.kind === 'visitor') return Boolean(a.siteToken) && Boolean(a.visitorId);
      return false;
    } catch {
      return false;
    }
  }

  socket.on('connect', () => {
    setConnection(conditions.disconnected ? 'disconnected' : 'open');
    consecutiveErrors = 0;
    // Flush anything queued while disconnected.
    const queued = pendingOutbox.splice(0);
    for (const event of queued) sendNow(event);
    for (const pending of reliableOutbox.values()) {
      if (pending.timer) clearTimeout(pending.timer);
      pending.timer = undefined;
      pending.inFlight = false;
      sendReliable(pending);
    }
  });
  socket.on('disconnect', () => {
    if (closed) return;
    for (const pending of reliableOutbox.values()) pending.inFlight = false;
    setConnection('closed');
  });
  // Failures during the "not yet logged in" or "server booting" window are
  // expected, so the first few stay at debug. But a connection that never
  // establishes — the signature of a reverse proxy not forwarding the WebSocket
  // upgrade, since there is no polling fallback — was invisible at debug level
  // and surfaced to users only as "chat is slow". After a handful of
  // consecutive failures, escalate to a warning that names the likely cause.
  let consecutiveErrors = 0;
  const ERROR_WARN_AFTER = 5;
  socket.on('connect_error', (err) => {
    if (closed) return;
    consecutiveErrors += 1;
    if (consecutiveErrors === ERROR_WARN_AFTER) {
      console.warn(
        `[socket-bus] still cannot connect after ${consecutiveErrors} attempts: ${err.message}. ` +
          'This transport is websocket-only — check that the reverse proxy forwards ' +
          'Upgrade/Connection headers on /socket.io/ and that proxy_read_timeout ' +
          'exceeds the ping interval.',
      );
    } else {
       
      console.debug('[socket-bus] connect_error', err.message);
    }
    setConnection('closed');
  });
  socket.on(BUS_CHANNEL, (event: BusEvent) => {
    if (conditions.disconnected) return;
    if (!event || typeof event.type !== 'string') return;
    dispatch(event);
  });

  // Only connect if we have something the server will accept. Otherwise
  // stay quiet until the caller invokes `bus.reconnect()` after auth arrives
  // (e.g. agent successfully logs in), or — with deferConnect — until the
  // caller decides the visitor has actually engaged.
  if (config.deferConnect) {
    setConnection('closed');
  } else if (hasValidAuth()) {
    socket.connect();
  } else {
    setConnection('closed');
  }

  function dispatch(event: BusEvent) {
    const set = listeners.get(event.type);
    if (!set || set.size === 0) return;
    [...set].forEach((fn) => {
      try {
        fn(event);
      } catch (err) {
         
        console.error('[socket-bus] listener threw', err);
      }
    });
  }

  function chooseLatency(): number {
    const { minLatency, maxLatency } = conditions;
    if (maxLatency <= minLatency) return Math.max(0, minLatency);
    return minLatency + Math.random() * (maxLatency - minLatency);
  }

  function scheduleRetry(key: string, pending: PendingDelivery) {
    if (closed || !reliableOutbox.has(key) || pending.timer) return;
    const delay = Math.min(500 * 2 ** Math.min(pending.attempts, 5), RETRY_MAX_MS);
    pending.timer = setTimeout(() => {
      pending.timer = undefined;
      pending.inFlight = false;
      sendReliable(pending);
    }, delay);
  }

  function sendReliable(pending: PendingDelivery) {
    const key = deliveryKey(pending.event);
    if (!key || closed || pending.inFlight) return;
    if (!socket.connected || conditions.disconnected) return;
    pending.inFlight = true;
    pending.attempts += 1;
    pending.generation += 1;
    const generation = pending.generation;
    socket.timeout(ACK_TIMEOUT_MS).emit(
      BUS_CHANNEL,
      pending.event,
      (err: Error | null, response?: AckResponse) => {
        if (generation !== pending.generation) return;
        pending.inFlight = false;
        if (!reliableOutbox.has(key)) return;
        if (!err && response?.ok) {
          if (pending.timer) clearTimeout(pending.timer);
          settle(key);
          config.onCommitted?.(key, response.sequence);
          return;
        }
        // Invalid/forbidden payloads cannot become valid by retrying. Every
        // transient failure (timeout, disconnect, rate-limit, DB error) stays
        // in the outbox until a server commit is acknowledged.
        if (!err && (response?.reason === 'invalid-payload' || response?.reason === 'forbidden')) {
          settle(key);
          return;
        }
        scheduleRetry(key, pending);
      },
    );
  }

  function persistOutbox() {
    if (!config.outboxStorage) return;
    try {
      config.outboxStorage.save([...reliableOutbox.values()].map((pending) => pending.event));
    } catch {
      // Storage full or blocked: delivery still works in memory.
    }
  }

  function settle(key: string) {
    reliableOutbox.delete(key);
    persistOutbox();
  }

  function queueReliable(event: BusEvent, key: string): PendingDelivery {
    let pending = reliableOutbox.get(key);
    if (!pending) {
      pending = { event, attempts: 0, generation: 0, inFlight: false };
      reliableOutbox.set(key, pending);
      persistOutbox();
    }
    return pending;
  }

  function sendNow(event: BusEvent) {
    if (closed) return;
    const key = deliveryKey(event);
    if (key) {
      const pending = queueReliable(event, key);
      sendReliable(pending);
      return;
    }
    if (!socket.connected) {
      pendingOutbox.push(event);
      return;
    }
    socket.emit(BUS_CHANNEL, event);
  }

  function emitWithLatency(event: BusEvent) {
    if (closed) return;
    const key = deliveryKey(event);
    // Optimistic local rendering happens exactly once; retries below never
    // redispatch locally.
    dispatch(event);
    const reliable = key ? queueReliable(event, key) : null;
    if (conditions.disconnected) return;
    if (Math.random() < conditions.packetLoss) {
      if (key && reliable) scheduleRetry(key, reliable);
      return;
    }
    const delay = chooseLatency();
    const fire = () => {
      if (closed || conditions.disconnected) return;
      sendNow(event);
    };
    if (delay <= 0) fire();
    else setTimeout(fire, delay);
  }

  function notifyConditionsChange() {
    const snapshot = { ...conditions };
    [...conditionListeners].forEach((fn) => fn(snapshot));
  }

  return {
    emit(event) {
      emitWithLatency(event as BusEvent);
    },
    on(name, listener) {
      let set = listeners.get(name);
      if (!set) {
        set = new Set();
        listeners.set(name, set);
      }
      const fn = listener as AnyBusListener;
      set.add(fn);
      return () => {
        set!.delete(fn);
      };
    },
    once(name, listener) {
      const fn = listener as AnyBusListener;
      let set = listeners.get(name);
      if (!set) {
        set = new Set();
        listeners.set(name, set);
      }
      const wrapped: AnyBusListener = (event) => {
        off();
        fn(event);
      };
      set.add(wrapped);
      const off = () => {
        set!.delete(wrapped);
      };
      return off;
    },
    off(name, listener) {
      const set = listeners.get(name);
      set?.delete(listener as AnyBusListener);
    },
    close() {
      closed = true;
      for (const pending of reliableOutbox.values()) {
        if (pending.timer) clearTimeout(pending.timer);
      }
      reliableOutbox.clear();
      pendingOutbox.length = 0;
      listeners.clear();
      conditionListeners.clear();
      connectionListeners.clear();
      try {
        socket.disconnect();
      } catch {
        /* ignore */
      }
    },
    reconnect() {
      if (closed) return;
      if (!hasValidAuth()) return;
      if (socket.connected) return;
      // socket.io re-reads the auth callback on every connect.
      socket.connect();
    },
    setNetworkConditions(patch) {
      conditions = { ...conditions, ...patch };
      if (conditions.disconnected) {
        setConnection('disconnected');
        socket.disconnect();
      } else if (!socket.connected) {
        socket.connect();
      } else {
        setConnection('open');
      }
      notifyConditionsChange();
    },
    getNetworkConditions() {
      return { ...conditions };
    },
    inspect() {
      const listenerCount: Record<string, number> = {};
      for (const [n, set] of listeners) listenerCount[n] = set.size;
      return { listenerCount, connected: connection === 'open' };
    },
    onConditionsChange(listener) {
      conditionListeners.add(listener);
      return () => conditionListeners.delete(listener);
    },
    onConnectionChange(listener) {
      connectionListeners.add(listener);
      try {
        listener(connection);
      } catch {
        /* ignore */
      }
      return () => connectionListeners.delete(listener);
    },
    getConnectionState() {
      return connection;
    },
  };
}
